/**
 * VÉRAULT Portal — Client: Your Journey (landing page).
 * Lists the client's trips as editorial cards. Each card → /portal/client/trip/:id.
 */
import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, ScrollText, CheckCircle2, ClipboardList, Sparkles } from "lucide-react";
import {
  Surface,
  Heading,
  PageHeader,
  EditorialRule,
  StatusTag,
  PortalButton,
  Empty,
  Skeleton,
  api,
  fmt,
  motion as portalMotion,
} from "../../index";
import { useAuth } from "../../../contexts/AuthContext";
import { RequestTripModal } from "../../components/RequestTripModal";

const { fadeInUp, stagger } = portalMotion;

// Editorial cross-wire: surface the client's acknowledged agreements +
// onboarding progress at-a-glance on the landing page. Quiet, calm — these
// only appear if there's something to show.
function TrustSignalsSummary() {
  const [agreements, setAgreements] = React.useState(null);
  const [forms, setForms] = React.useState(null);

  React.useEffect(() => {
    let alive = true;
    Promise.allSettled([
      api.get("/client-portal/agreements"),
      api.get("/client-portal/forms"),
    ]).then(([ra, rf]) => {
      if (!alive) return;
      setAgreements(ra.status === "fulfilled" && Array.isArray(ra.value.data) ? ra.value.data : []);
      setForms(rf.status === "fulfilled" && Array.isArray(rf.value.data) ? rf.value.data : []);
    });
    return () => { alive = false; };
  }, []);

  if (agreements === null || forms === null) return null;
  const acked = agreements.filter((a) => a.status === "acknowledged" || a.status === "signed");
  const pendingAck = agreements.filter((a) => a.status === "sent");
  const activePacket = forms.find((p) => p.status === "in_progress" || p.status === "sent");
  const completedPacket = forms.find((p) => p.status === "completed");
  if (acked.length === 0 && pendingAck.length === 0 && !activePacket && !completedPacket) return null;

  return (
    <motion.div variants={fadeInUp} data-testid="client-trust-signals">
      <EditorialRule label="On the record" />
      <div className="mt-5 grid sm:grid-cols-2 gap-4 sm:gap-5">
        {(acked.length > 0 || pendingAck.length > 0) && (
          <Surface variant="default" padded className="!p-5" data-testid="client-agreements-card">
            <div className="flex items-center gap-2 mb-3">
              <ScrollText className="size-4 text-[hsl(var(--accent-gold))]" strokeWidth={1.5} />
              <Heading level="eyebrow">
                Agreements · {acked.length} signed{pendingAck.length ? ` · ${pendingAck.length} awaiting` : ""}
              </Heading>
            </div>
            {acked.length > 0 && (
              <ul className="space-y-1.5">
                {acked.slice(0, 4).map((a) => (
                  <li key={a.id} className="flex items-start gap-2" data-testid={`client-agreement-${a.id}`}>
                    <CheckCircle2 className="size-3.5 mt-0.5 text-[hsl(var(--accent-sage,170_18%_42%))] shrink-0" strokeWidth={1.5} />
                    <div className="min-w-0 flex-1">
                      <p className="font-body text-[13.5px] truncate">{a.title || a.type}</p>
                      <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">
                        {[a.trip_name, a.acknowledged_at ? `Acknowledged ${fmt.formatDate(a.acknowledged_at)}` : null].filter(Boolean).join(" · ")}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            {pendingAck.length > 0 && (
              <ul className={`${acked.length ? "mt-3 pt-3 border-t border-[rgba(0,0,0,0.05)]" : ""} space-y-1.5`}>
                {pendingAck.slice(0, 3).map((a) => (
                  <li key={a.id} className="flex items-start gap-2">
                    <span className="size-3.5 mt-0.5 rounded-full border border-[hsl(var(--accent-gold))] shrink-0" />
                    <p className="font-body text-[13.5px] truncate">
                      {a.title || a.type}
                      <span className="ml-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--accent-gold))]">awaiting you</span>
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </Surface>
        )}
        {(activePacket || completedPacket) && (
          <Surface variant="default" padded className="!p-5" data-testid="client-onboarding-card">
            <div className="flex items-center gap-2 mb-3">
              <ClipboardList className="size-4 text-[hsl(var(--accent-gold))]" strokeWidth={1.5} />
              <Heading level="eyebrow">Onboarding</Heading>
            </div>
            {completedPacket && (
              <div className="flex items-start gap-2">
                <CheckCircle2 className="size-3.5 mt-0.5 text-[hsl(var(--accent-sage,170_18%_42%))] shrink-0" strokeWidth={1.5} />
                <div>
                  <p className="font-body text-[13.5px]">{completedPacket.section_slugs?.length || 0} sections complete</p>
                  <p className="font-accent italic text-[12.5px] text-[hsl(var(--foreground-muted))]">
                    Thank you — your advisor has everything.
                  </p>
                </div>
              </div>
            )}
            {activePacket && !completedPacket && (
              <div>
                <p className="font-body text-[13.5px]">
                  {activePacket.completed_section_slugs?.length || 0}/{activePacket.section_slugs?.length || 0} sections done
                </p>
                <p className="font-accent italic text-[12.5px] text-[hsl(var(--foreground-muted))] mt-1">
                  Pick up where you left off via the link your advisor sent.
                </p>
              </div>
            )}
          </Surface>
        )}
      </div>
    </motion.div>
  );
}

export default function ClientJourney() {
  const { user } = useAuth() || {};
  const GENERIC = new Set(["owner", "admin", "advisor", "user", "client", "developer", "dev"]);
  const rawName = (user?.name || "").split(/\s+/)[0];
  const name = rawName && !GENERIC.has(rawName.toLowerCase()) ? rawName : "";
  const [trips, setTrips] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [requestOpen, setRequestOpen] = React.useState(false);

  React.useEffect(() => {
    let alive = true;
    api
      .get("/client-portal/trips")
      .then(({ data }) => alive && setTrips(Array.isArray(data) ? data : []))
      .catch(() => alive && setError("Couldn't load your trips."));
    return () => {
      alive = false;
    };
  }, []);

  return (
    <motion.div initial="initial" animate="animate" variants={stagger} className="space-y-12">
      <motion.div variants={fadeInUp}>
        <PageHeader
          eyebrow="Home"
          title={name ? `Welcome back, ${name}.` : "Welcome back."}
          lede="Your experiences, kept close."
          actions={
            <PortalButton
              variant="gold"
              size="sm"
              icon={<Sparkles className="size-3.5" strokeWidth={1.5} />}
              onClick={() => setRequestOpen(true)}
              data-testid="journey-request-trip-cta"
            >
              Request a trip
            </PortalButton>
          }
        />
      </motion.div>

      <TrustSignalsSummary />

      <RequestTripModal
        open={requestOpen}
        onClose={() => setRequestOpen(false)}
      />

      <motion.div variants={fadeInUp}>
        <EditorialRule label={trips ? `${trips.length} experience${trips.length === 1 ? "" : "s"}` : "loading"} />
        <div className="mt-6">
          {error ? (
            <Empty title={error} icon={<MapPin className="size-6" strokeWidth={1.25} />} />
          ) : !trips ? (
            <div className="grid sm:grid-cols-2 gap-5">
              {[0, 1].map((i) => (
                <Skeleton key={i} className="h-48" />
              ))}
            </div>
          ) : trips.length === 0 ? (
            <Empty
              title="No experiences yet"
              hint="Your advisor will share your trip here when it's ready."
              icon={<MapPin className="size-6" strokeWidth={1.25} />}
            />
          ) : (
            <ul className="grid sm:grid-cols-2 gap-5" data-testid="client-trips-grid">
              {trips.map((t) => (
                <li key={t.id}>
                  <Link
                    to={`/portal/client/trip/${t.id}`}
                    className="group block"
                    data-testid={`client-trip-${t.id}`}
                  >
                    <Surface variant="default" padded={false} className="overflow-hidden transition-all duration-300 group-hover:-translate-y-0.5 group-hover:shadow-floating">
                      <div className="aspect-[16/10] relative bg-[hsl(var(--background-stone))]">
                        {t.hero_image_url ? (
                          <img
                            src={t.hero_image_url}
                            alt={t.client_trip_title || t.destinations || "Trip"}
                            className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.4s] group-hover:scale-105"
                            loading="lazy"
                          />
                        ) : (
                          <div className="absolute inset-0 bg-gradient-to-br from-[hsl(var(--background-paper))] to-[hsl(var(--background-stone))]" />
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/35 via-transparent to-transparent" />
                      </div>
                      <div className="p-5">
                        <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
                          {(t.reveal_destination || "full") !== "full" ? "Awaiting reveal" : "Experience"}
                        </p>
                        <p className="mt-1.5 font-serif text-xl text-[hsl(var(--foreground))] truncate">
                          {(t.reveal_destination || "full") === "full"
                            ? (t.client_trip_title || t.destinations || "Untitled")
                            : (t.reveal_destination_mask || "A surprise ✨")}
                        </p>
                        <p className="mt-1 font-accent italic text-[13px] text-[hsl(var(--foreground-secondary))]">
                          {(t.reveal_dates || "full") === "full"
                            ? fmt.formatDateRange(t.trip_start_date || t.start_date, t.trip_end_date || t.end_date)
                            : (t.reveal_dates_mask || "Coming soon")}
                        </p>
                      </div>
                    </Surface>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}
